import { useCallback, useEffect, useState } from 'react';
import { ApiFailure } from '../lib/api';
import { cachedGet } from '../lib/cache';
import { outbox } from '../lib/outbox';
import { getPosition } from '../lib/native';
import type { CustomerGroup, PickupGroup } from '../lib/types';
import { PhotoPicker, type UploadedPhoto } from '../components/PhotoPicker';
import { openNavigation, launch, navPreference, type NavOption } from '../lib/navigate';
import { useShowFuture } from '../lib/showFuture';
import { Camera, CircleParking, CloudOff, Factory, MessageCircle, Navigation, Package, Store } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

/**
 * 取货 —— 按取货点分组,组内按商家分组。
 *
 * 司机看到的是「本店 · 3 单」而不是 3 条「去本店取货」。
 * 组内按商家分,是因为**拍照要求按商家算** —— 同一个仓库里
 * 商家 A 要拍、商家 B 不要拍是常态。
 * 见 docs/14-driver-ux-spec.md §14.5
 */
export function PickupTasks({ onClose }: { onClose: () => void }) {
  const [groups, setGroups] = useState<PickupGroup[] | null>(null);
  const [active, setActive] = useState<PickupGroup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [staleAt, setStaleAt] = useState<number | null>(null);
  /*
   * ★ 取货这一屏也得跟着顶栏那个「非当日达」走。
   *
   *   不跟的话就会出现最难解释的那种不一致:今日列表里 8 单,
   *   点进取货是 11 单 —— 而司机没有任何办法知道多出来的三单
   *   是明天的。顶栏那个勾选框是**全局**的,就该全局都算数。
   */
  const [showFuture] = useShowFuture();

  const load = useCallback(async () => {
    try {
      // ★ 走缓存:司机常常在没信号的仓库里打开这一页。
      //   拿不到网络就用上次的数据,总比「加载失败」强
      // ★ 第二个参数是离线退路 —— 缓存按完整路径存,勾上之后那条
      //   带参数的地址可能从来没缓存过(见 cachedGet 里的说明)
      const qs = showFuture ? '?includeFuture=1' : '';
      const r = await cachedGet<{ groups: PickupGroup[] }>(
        `/driver/pickup-tasks${qs}`,
        '/driver/pickup-tasks',
      );
      setGroups(r.data.groups);
      setStaleAt(r.stale ? r.cachedAt : null);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    }
  }, [showFuture]);

  useEffect(() => {
    void load();
  }, [load]);

  if (active) {
    return (
      <PickupDetail
        group={active}
        onBack={() => setActive(null)}
        onDone={async () => {
          setActive(null);
          await load();
        }}
      />
    );
  }

  return (
    <div className="pb-24">
      <div className="flex items-center gap-2 px-4 py-3">
        <button onClick={onClose} className="text-2xl text-slate-400 -ml-1">‹</button>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">待取货</h2>
      </div>

      {error && <div className="mx-4 text-red-600 dark:text-red-400">{error}</div>}

      {/* ★ 用的是旧数据就必须说清楚。司机据此决定要不要相信「3 单」这个数字 */}
      {staleAt != null && (
        <p className="mx-4 mb-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm
                      text-amber-800 dark:text-amber-300">
          <InlineIcon icon={CloudOff} /> 离线数据 · 更新于{' '}
          {new Date(staleAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}

      {groups?.length === 0 && (
        <div className="mx-4 mt-6 rounded-2xl border border-dashed border-slate-300
                        dark:border-slate-700 p-10 text-center text-slate-400">
          没有待取货的订单
        </div>
      )}

      <div className="px-4 space-y-3">
        {groups?.map((g) => (
          <GroupCard key={g.pickupPointId ?? g.name} group={g} onOpen={() => setActive(g)} />
        ))}
      </div>
    </div>
  );
}

function GroupCard({ group: g, onOpen }: { group: PickupGroup; onOpen: () => void }) {
  const [navChoices, setNavChoices] = useState<NavOption[] | null>(null);
  const icon = g.type === 'WAREHOUSE' ? Factory : g.type === 'MERCHANT' ? Store : Package;

  return (
    <>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800
                      bg-white dark:bg-slate-800/50 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            <InlineIcon icon={icon} /> {g.name}
          </h3>
          <span className="shrink-0 rounded-full bg-brand-600 text-white text-sm px-2.5 py-0.5 font-medium">
            {g.orderCount} 单
          </span>
        </div>

        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {g.address}
          {g.distanceM != null && ` · ${(g.distanceM / 1000).toFixed(1)}km`}
        </p>

        {g.cutoffTime && <CutoffWarning time={g.cutoffTime} />}

        {/* 新司机第一次来最需要的两条,放在卡片正面不藏二级页 */}
        {g.pickupInstruction && (
          <p className="mt-2 rounded-lg bg-blue-50 dark:bg-blue-950/40 px-3 py-2 text-sm
                        text-blue-800 dark:text-blue-200">
            <InlineIcon icon={MessageCircle} /> {g.pickupInstruction}
          </p>
        )}
        {g.parkingNote && (
          <p className="mt-1.5 text-sm text-slate-500"><InlineIcon icon={CircleParking} /> {g.parkingNote}</p>
        )}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              if (g.lat == null || g.lng == null) return;
              const opts = openNavigation(g.lat, g.lng, g.name);
              if (opts) setNavChoices(opts);
            }}
            disabled={g.lat == null}
            className="h-13 py-3 rounded-xl border border-slate-300 dark:border-slate-600
                       text-slate-900 dark:text-slate-100 disabled:opacity-40"
          >
            <InlineIcon icon={Navigation} /> 导航
          </button>
          <button
            onClick={onOpen}
            className="h-13 py-3 rounded-xl bg-brand-600 text-white font-medium active:bg-brand-700"
          >
            <InlineIcon icon={Package} /> 我到了
          </button>
        </div>
      </div>

      {navChoices && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setNavChoices(null)}>
          <div className="w-full rounded-t-2xl bg-white dark:bg-slate-800 p-4 pb-safe"
               onClick={(e) => e.stopPropagation()}>
            {navChoices.map((o) => (
              <button
                key={o.key}
                onClick={() => { navPreference.set(o.key); setNavChoices(null); launch(o); }}
                className="w-full h-14 rounded-xl border border-slate-200 dark:border-slate-700
                           text-left px-4 text-lg mb-2 text-slate-900 dark:text-slate-100"
              >
                {o.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/** 截单时间预警 —— 临近时变红 */
function CutoffWarning({ time }: { time: string }) {
  const [h, m] = time.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(h ?? 0, m ?? 0, 0, 0);
  const leftMin = Math.round((cutoff.getTime() - Date.now()) / 60_000);

  if (leftMin < 0) {
    return <p className="mt-1 text-sm text-red-600 dark:text-red-400">⚠ 已过截单时间 {time}</p>;
  }
  if (leftMin > 120) return null;
  return (
    <p className={`mt-1 text-sm ${leftMin < 30 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
      ⚠ {time} 截单(剩 {Math.floor(leftMin / 60) > 0 ? `${Math.floor(leftMin / 60)} 小时 ` : ''}{leftMin % 60} 分)
    </p>
  );
}

/** 取货详情 —— 按商家分组勾选,需要拍照的组单独标出 */
function PickupDetail({
  group: g,
  onBack,
  onDone,
}: {
  group: PickupGroup;
  onBack: () => void;
  onDone: () => Promise<void>;
}) {
  // 默认全选:司机通常全部取走,取消个别的比勾选全部快
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(g.customerGroups.flatMap((c) => c.orders.map((o) => o.id))),
  );
  const [photosByCustomer, setPhotos] = useState<Record<string, UploadedPhoto[]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geoDenied, setGeoDenied] = useState(false);

  /*
   * ★ 位置权限被拒的话,**在他动手之前**就说。
   *
   *   等到提交才发现的话,他已经拍完照、点完确认 —— 白干一趟,
   *   而且当时人可能已经离开柜台了。这件事进这一页时就是已知的:
   *   接口早就给了 `geofence.required`,浏览器也能直接查权限状态。
   *
   *   查不到就不猜(有些 WebView 没有 permissions API)——
   *   那种情况下仍然靠提交时的兜底提示。
   */
  useEffect(() => {
    if (!g.geofence.required) return;
    if (!navigator.permissions?.query) return;
    let alive = true;
    void navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((st) => {
        if (alive) setGeoDenied(st.state === 'denied');
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [g.geofence.required]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  /** 某个商家组是否满足拍照要求 */
  function groupReady(cg: CustomerGroup): boolean {
    const picked = cg.orders.filter((o) => selected.has(o.id));
    if (picked.length === 0) return true; // 全没选,不参与提交
    if (!cg.proof.required) return true;
    return (photosByCustomer[cg.customerId]?.length ?? 0) >= cg.proof.minPhotos;
  }

  const blockedGroups = g.customerGroups.filter((cg) => !groupReady(cg));
  const readyIds = g.customerGroups
    .filter(groupReady)
    .flatMap((cg) => cg.orders.filter((o) => selected.has(o.id)).map((o) => o.id));

  async function submit(ids: string[]) {
    if (ids.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const geo = await getPosition();
      // 照片按商家分槽,同步时上传完再回填进 payload.proofsByCustomer
      const photoSlots = Object.entries(photosByCustomer).flatMap(([cid, ps]) =>
        ps.map((p) => ({ slot: `customer:${cid}`, purpose: 'PICKUP_PHOTO', blob: p.blob })),
      );

      /*
       * ★ 定位拿不到、而这个取货点又要求围栏时,**当场说**。
       *
       *   服务端会拒(GEOFENCE_VIOLATION),但那是提交之后的事 ——
       *   司机已经拍完照、点完确认,才发现白干。而这件事在他点之前
       *   就是已知的:接口早就告诉了我们 `geofence.required`,
       *   只是以前没人用这个字段。
       */
      if (!geo && g.geofence.required) {
        setError(
          '这个取货点要求到店确认,而现在拿不到你的位置 —— ' +
            '请在系统设置里给「位置」权限,然后重试。照片不会丢。',
        );
        return;
      }

      // 取货整批进队列 —— 仓库内部经常没信号,司机拍完照就该能走
      const queuedId = await outbox.enqueue({
        orderIds: ids,
        action: 'PICKUP',
        endpoint: '/orders/batch-transition',
        body: {
          action: 'PICKUP',
          orderIds: ids,
          batchKey: '',  // outbox 用自己的 id 覆盖
          ...(geo ? { geo } : {}),
          /*
           * ★ 没有取货点(AD_HOC:单子上直接写的发货地址)就**不带**这个字段。
           *   原来原样传 null,服务端只认字符串 → 整批「请求参数校验失败」。
           *   绑了取货点的组没事,所以表现成「有的地址能取、有的不能」。
           */
          payload: g.pickupPointId ? { pickupPointId: g.pickupPointId } : {},
        },
        photos: photoSlots,
      });

      /*
       * ★ 等第一次同步的结果再离开这一页。
       *
       *   队列的设计是「点完就能走」,那是为了没信号的场景。
       *   但他现在有信号、而且**人就站在店里等结果** ——
       *   被拒了却跳回列表的话,他以为成了,订单其实一直没动。
       *   这就是用户报的「司机无法更新订单状态」:
       *   系统各层都拦对了,只是没有一层把话说给他听。
       */
      const left = await outbox.settle(queuedId);
      if (left?.error) {
        setError(left.error.message);
        /*
         * ★ 报完就把队列里那一条**撤掉**。
         *
         *   不撤的话同一件事会在两个地方各说一次:这一页的红字,
         *   和顶部那条常驻的「N 项操作反复失败」横幅 ——
         *   而横幅上那个「重试」会把**一模一样的请求**再发一次
         *   (照片和定位都还是老样子),于是又失败一次。
         *
         *   他人就在这一页、照片和勾选都还在,重来一次的入口就是
         *   下面那个按钮。这一条的责任已经由这一页接过来了。
         */
        await outbox.discard(queuedId);
        return; // 留在这一页 —— 他要处理的东西都还在
      }
      await onDone();
    } catch (e) {
      setError((e as Error).message || '提交失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pb-40">
      <div className="flex items-center gap-2 px-4 py-3">
        <button onClick={onBack} className="text-2xl text-slate-400 -ml-1">‹</button>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          取货 · {g.name}
        </h2>
      </div>

      {error && (
        <div className="mx-4 mb-3 rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/*
        ★ 权限已经被拒的话,进这一页就说 —— 不要等他拍完照点了确认。
          那时候他已经白干一趟,人可能也离开柜台了。
      */}
      {geoDenied && !error && (
        <div className="mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3
                        text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          本店要求到店确认,而位置权限被拒了 —— 先在系统设置里打开「位置」,
          否则拍完照也提交不了。
        </div>
      )}

      <div className="px-4 space-y-4">
        {g.customerGroups.map((cg) => {
          const photos = photosByCustomer[cg.customerId] ?? [];
          const need = cg.proof.required;
          const missing = need && photos.length < cg.proof.minPhotos;

          return (
            <div
              key={cg.customerId}
              className={`rounded-2xl border p-4 ${
                missing
                  ? 'border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20'
                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                  ▼ {cg.customerName}({cg.orders.length} 单)
                </h3>
                <span className={`text-sm ${need ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`}>
                  {need ? <><InlineIcon icon={Camera} /> 需拍照</> : '无需拍照'}
                </span>
              </div>

              <ul className="mt-2 space-y-1">
                {cg.orders.map((o) => (
                  <li key={o.id}>
                    <label className="flex items-center gap-3 py-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.has(o.id)}
                        onChange={() => toggle(o.id)}
                        className="w-5 h-5"
                      />
                      <span className="flex-1 text-slate-900 dark:text-slate-100">
                        {o.receiverName}
                      </span>
                      <span className="text-xs text-slate-400">{o.orderNo}</span>
                    </label>
                  </li>
                ))}
              </ul>

              {need && (
                <div className="mt-3">
                  {/* 商家自定义的提示直接显示在拍照区旁 */}
                  {cg.proof.hint && (
                    <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">
                      <InlineIcon icon={MessageCircle} /> {cg.proof.hint}
                    </p>
                  )}
                  <PhotoPicker
                    purpose="PICKUP_PHOTO"
                    max={cg.proof.maxPhotos}
                    photos={photos}
                    onChange={(p) => setPhotos((prev) => ({ ...prev, [cg.customerId]: p }))}
                  />
                  {cg.proof.mode === 'PER_BATCH' && (
                    <p className="mt-1.5 text-xs text-slate-400">
                      一张合影即可覆盖该商家全部订单
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 底部固定操作区。★ 不因为一组缺照片就卡住全部 —— 给"仅提交 X 单"的出路 */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-slate-800 border-t
                      border-slate-200 dark:border-slate-700 p-4 pb-safe space-y-2">
        {blockedGroups.length > 0 && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            ⚠ {blockedGroups.map((c) => c.customerName).join('、')} 需要拍照后才能提交
          </p>
        )}
        {blockedGroups.length > 0 && readyIds.length > 0 && (
          <button
            onClick={() => void submit(readyIds)}
            disabled={busy}
            className="w-full h-13 py-3 rounded-xl border border-slate-300 dark:border-slate-600
                       text-slate-900 dark:text-slate-100 disabled:opacity-40"
          >
            仅提交 {readyIds.length} 单
          </button>
        )}
        <button
          onClick={() => void submit(readyIds)}
          disabled={busy || blockedGroups.length > 0 || readyIds.length === 0}
          className="w-full h-14 rounded-xl bg-brand-600 text-white text-lg font-medium
                     disabled:opacity-40 active:bg-brand-700"
        >
          {/*
            ★ 按钮上的字要和拦住他的原因**说同一件事**。
              原来写的是「确认取货 0 单」—— 而这一组明明有单,
              只是还差一张照片。0 这个数字读起来是「没东西可取」,
              和上面那行「需要拍照后才能提交」互相矛盾;
              人看到 0 就不会再去找那个只有图标的拍照按钮了。
          */}
          {busy
            ? '提交中…'
            : readyIds.length === 0 && blockedGroups.length > 0
              ? '先拍商品照才能取货'
              : `确认取货 ${readyIds.length} 单`}
        </button>
      </div>
    </div>
  );
}

